import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { STAGES } from '@joker/domain'
import type { BucketSummary } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { ErrorNote, Loading, Panel, ViewTitle } from '../ui'
import { CohortEditor, describeDraft, ruleToDraft } from './CohortEditor'
import { CohortFunnel } from './CohortFunnel'

/**
 * One cohort and what can be done with it. The group is defined once; each
 * action reads the same membership, so the list, the CSV, the training numbers
 * and (next) the funnel all describe the same people.
 */
export function CohortPage({
  id,
  onBack,
  onOpenMembers,
  onOpenFunnelList,
}: {
  id: string
  onBack: () => void
  onOpenMembers: (b: BucketSummary) => void
  /** Opens the Users list for one number on the funnel (a formatFunnelPick string). */
  onOpenFunnelList: (pick: string) => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const list = useQuery(trpc.buckets.list.queryOptions())
  const options = useQuery(trpc.buckets.options.queryOptions())
  const [editing, setEditing] = useState(false)
  const archive = useMutation(
    trpc.buckets.archive.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries()
        onBack()
      },
    }),
  )

  if (list.error) return <ErrorNote error={list.error} />
  if (!list.data) return <Loading what="the cohort" />
  const b = list.data.find((x) => x.id === id)
  if (!b) {
    return (
      <>
        <button type="button" className="back" onClick={onBack}>
          ← Cohorts
        </button>
        <p className="footnote">This cohort does not exist, or it was archived.</p>
      </>
    )
  }

  const draft = ruleToDraft(b.rule, b.name, b.description, b.audience)
  const words = draft ? describeDraft(draft, options.data) : b.ruleDescription
  const goal = b.goalStage ? STAGES.find((s) => s.id === b.goalStage)?.label : b.goalNote

  return (
    <>
      <button type="button" className="back" onClick={onBack}>
        ← Cohorts
      </button>
      <ViewTitle suit="heart">{b.name}</ViewTitle>
      <p className="view-sub">
        <b className="num">{b.memberCount.toLocaleString()}</b> {b.audience === 'prospects' ? 'leads' : 'users'}
        {b.system && ' · system cohort'}
        {b.description && b.description !== words && <> · {b.description}</>}
      </p>

      {editing && draft ? (
        <CohortEditor editing={b.id} initial={draft} onDone={() => setEditing(false)} />
      ) : (
        <Panel title="Who is in it" note={goal ? `goal → ${goal}` : undefined}>
          <p className="cohort-rule">{words}</p>
          <div className="bcard-actions">
            <button type="button" className="btn primary" onClick={() => onOpenMembers(b)}>
              View {b.memberCount === 1 ? 'member' : `all ${b.memberCount}`}
            </button>
            <a
              className="btn ghost"
              href={`/api/export/people.csv?bucketId=${encodeURIComponent(b.id)}&label=${encodeURIComponent(b.name)}`}
              download
            >
              <span aria-hidden="true">↓</span> Export CSV
            </a>
            <button
              type="button"
              className="btn ghost"
              onClick={() => setEditing(true)}
              disabled={!draft}
              title={draft ? undefined : 'This rule nests conditions more deeply than the editor shows; it is kept as it is.'}
            >
              Edit
            </button>
            {!b.system && (
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  if (window.confirm(`Archive "${b.name}"? It leaves the list; its history is kept.`)) archive.mutate({ id: b.id })
                }}
              >
                Archive
              </button>
            )}
          </div>
          {!draft && (
            <p className="footnote">
              This rule nests conditions more deeply than the editor shows, so it is kept as it is rather than rewritten.
            </p>
          )}
          {archive.error && <ErrorNote error={archive.error} />}
        </Panel>
      )}

      {b.audience === 'patients' && <CohortFunnel id={b.id} onOpenList={onOpenFunnelList} />}

      {b.audience === 'patients' && <Training id={b.id} />}


      <Panel title="Send to Klaviyo" note="phase 2">
        <p className="footnote">
          Keeping a Klaviyo list in step with this cohort, so a flow can greet people as they join
          it. Joker sends nothing in phase 1.
        </p>
      </Panel>
    </>
  )
}

/** The cohort's paid members against the training milestones, beside everybody who paid. */
function Training({ id }: { id: string }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.analytics.cohortTraining.queryOptions({ bucketId: id }))
  return (
    <Panel title="Training" note="share of paid members who reached each milestone">
      {q.error ? (
        <ErrorNote error={q.error} />
      ) : !q.data ? (
        <Loading what="training" />
      ) : q.data.rows[0]!.n === 0 ? (
        <p className="footnote">Nobody in this cohort has paid, so there is no training to compare.</p>
      ) : (
        <div className="scroll-x">
          <table className="mx">
            <thead>
              <tr>
                <th className="rowh"></th>
                {q.data.rows[0]!.milestones.map((m) => (
                  <th key={m.sessions}>{m.sessions === 1 ? 'Started' : `${m.sessions}+`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((row) => (
                <tr key={row.key}>
                  <td className="rowh">
                    {row.key}
                    <span className="n num">n={row.n}</span>
                  </td>
                  {row.milestones.map((m) => (
                    <td key={m.sessions} className="cell" title={`${m.count} of ${row.n}`}>
                      {m.pct}%
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}
